import { PureComponent, isValidElement, createContext, useContext, useEffect } from 'react'
import type { PropsWithRef, PropsWithChildren, ErrorInfo } from 'react'

import Either from '@/components/Either'
import { Error } from '@/components/Render'

interface IFallbackProps {
  error: Error
  clearError: (...args: any[]) => void
}

interface IErrorBoundaryContext {
  error?: Error
  clearError: (...args: any[]) => void
}

interface IErrorBoundaryProps {
  onError?: (error: Error, info: ErrorInfo) => void
  onReset?: (...args: any[]) => IErrorBoundaryState
  fallbackRender?: (props: IFallbackProps) => ReactNode
}

interface IErrorBoundaryState {
  error?: Error
}

const initialState = {
  error: undefined,
}

const ErrorBoundaryContext = createContext<IErrorBoundaryContext>({} as IErrorBoundaryContext)

class StaticErrorBoundary extends PureComponent<
  PropsWithRef<PropsWithChildren<IErrorBoundaryProps>>,
  IErrorBoundaryState
> {
  state = initialState

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  clearError = (...args: any[]) => {
    const { error } = this.state
    if (!error) return
    this.props.onReset?.(...args)
    this.setState(initialState)
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.props.onError?.(error, info)
  }

  render() {
    const { children, fallbackRender } = this.props
    const { error } = this.state

    let fallbackNode = <Error error={error} hightLess />

    if (error) {
      const props: IFallbackProps = {
        error,
        clearError: this.clearError,
      }

      if (typeof fallbackRender === 'function') {
        const renderFallback = fallbackRender(props)
        if (isValidElement(renderFallback)) {
          fallbackNode = renderFallback
        }
      }
    }

    return (
      <ErrorBoundaryContext.Provider value={{ error: error, clearError: this.clearError }}>
        <Either condition={!error} fallback={fallbackNode}>
          {children}
        </Either>
      </ErrorBoundaryContext.Provider>
    )
  }
}

const useErrorBoundary = (config?: { onError?: (error: Error) => void }) => {
  const errorProps = useContext(ErrorBoundaryContext)

  useEffect(() => {
    if (!errorProps.error) return
    config?.onError?.(errorProps.error)
  }, [errorProps])

  return errorProps
}

export { StaticErrorBoundary, useErrorBoundary }
